/**
 * src/components/LatexEditor.tsx — the LaTeX source textarea used by BOTH editor tabs.
 *
 * Source: `D:/WCS/latex2html-french-trad/src/components/LatexEditor.tsx` (whole file, 46 lines).
 * Spec:   `docs/spec/06-frontend-components.md` §5.1; §15 D-15; §16 rank 18.
 *
 * Ported line for line. No line numbers, no syntax highlighting, no autosave, no dirty tracking.
 *
 * ═══ DF-22 RANK 18 (spec D-15) — THE TEXTAREA IS A KEYBOARD TRAP. REPRODUCED ON PURPOSE ══════
 * `LatexEditor.tsx:16-28` calls `e.preventDefault()` for **every** `Tab` keypress, so focus can
 * never leave the field with the keyboard. All four consequences are deliberate here:
 *   1. Tab inserts **two spaces**, never a tab character.
 *   2. A selection is **replaced** by the two spaces — there is no block indent.
 *   3. There is **no Shift+Tab outdent** (the handler does not read `e.shiftKey` at all).
 *   4. Native undo history is broken: the value is replaced programmatically through `onChange`,
 *      so Ctrl+Z cannot step back over an indent.
 * Do NOT add `e.shiftKey` handling, an Escape-then-Tab escape hatch, or a `tabIndex` change.
 *
 * ═══ THE LISTENER IS RE-ATTACHED ON EVERY KEYSTROKE — ALSO REPRODUCED ════════════════════════
 * The effect's dependency array is `[value, onChange]` (`LatexEditor.tsx:32`), and `value` changes
 * on every character. `addEventListener`/`removeEventListener` therefore run per keystroke. This is
 * load-bearing, not incidental: `handleKeyDown` closes over `value`, so a listener attached once
 * with `[]` deps would splice into a stale string and silently discard typing. Leave it.
 *
 * ═══ WHY `src/main.tsx` MUST NOT MOUNT `<StrictMode>` ════════════════════════════════════════
 * Spec §1.1: the source has no StrictMode. Under StrictMode this effect is attached, torn down and
 * re-attached on mount; that happens to be harmless here, but `HtmlPreview`'s `document.write`
 * effect is not (see that file). The shell owner drops StrictMode; this file assumes it is gone.
 */
import { useRef, useEffect } from 'react'

/** `LatexEditor.tsx:3-7`, field for field. */
interface LatexEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
}

/**
 * `LatexEditor.tsx:9-46`. The default placeholder string is verbatim from `:9`.
 *
 * Note that `ConverterApp` always passes an explicit `placeholder`, so this default is unreachable
 * in the shipped app — kept because it is part of the ported prop contract.
 */
export function LatexEditor({
  value,
  onChange,
  placeholder = 'Enter your LaTeX code here...',
}: LatexEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      // Handle tab key for indentation
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Tab') {
          // DF-22 rank 18 — unconditional preventDefault: this is the keyboard trap.
          e.preventDefault()
          const start = textarea.selectionStart
          const end = textarea.selectionEnd
          // DF-22 rank 18 — a selection is replaced by two spaces, never block-indented.
          const newValue = value.substring(0, start) + '  ' + value.substring(end)
          onChange(newValue)
          // Set cursor position after tab
          setTimeout(() => {
            textarea.selectionStart = textarea.selectionEnd = start + 2
          }, 0)
        }
      }
      textarea.addEventListener('keydown', handleKeyDown)
      return () => textarea.removeEventListener('keydown', handleKeyDown)
    }
  }, [value, onChange])

  return (
    <div className="h-full flex flex-col">
      {/*
        `editor-area` is a project utility class, not a Tailwind one:
        JetBrains Mono / 14px / line-height 1.6 / `hsl(var(--editor-bg))`
        (source `src/index.css:93-99`). It must exist in `src/index.css` for this editor to look
        like the shipped app — see the note at the end of this file.
      */}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 w-full p-4 resize-none editor-area focus:outline-none"
        placeholder={placeholder}
        spellCheck={false}
      />
    </div>
  )
}

/*
 * ── DEPENDENCY OWNED BY ANOTHER FILE ────────────────────────────────────────────────────────
 * `.editor-area` and the `--editor-bg` custom property are defined in the source's
 * `src/index.css` (`:40` and `:92-99`) and are currently **absent** from `D:/WCS/L2HC/src/index.css`.
 * Without them the textarea falls back to the page background and the browser default font, which
 * is a visual regression, not a reproduced defect. Reported to the coordinator; not fixed here
 * because `src/index.css` is not one of this agent's assigned files.
 */
